import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { Licence, ListResellerApplicationsResponse, ResellerApplication } from "@xop/dashboard-shared";

import type { ReleaseDTO } from "@crm-src/pages/releases/releases-api";

import { api } from "../../lib/api.js";

/**
 * Data for the Products & partners column of the Company pulse.
 *
 * Each hook reads the SAME dashboard BFF endpoint the matching CRM page reads,
 * which is the whole access story: `/api/licences` is gated on `crm:licences`
 * (and crm-api scopes the rows), `/api/admin/reseller-applications` on
 * `crm:reseller_applications`, `/api/documentation/admin/releases` on
 * `admin:users` | `crm:products_pricing`. `NeedsAttention` mounts a card only
 * for a holder of its grant, so a hook never fires for someone the server
 * would refuse — and if it did, the server would still refuse.
 *
 * Query keys live under `dashboard/watch/*`, NOT the CRM pages' keys: those
 * pages cache differently-shaped values (the releases page stores the nested
 * families), and sharing a key would hand one of them the other's shape.
 */

/** Licences, applications and releases move slowly; no need for the queues'
 * 20 s cadence. */
export const WATCH_REFRESH_MS = 60_000;

export const WATCH_QUERY_KEYS = {
  licences: ["dashboard", "watch", "licences"],
  applications: ["dashboard", "watch", "reseller-applications"],
  releases: ["dashboard", "watch", "releases"],
} as const;

const shared = {
  refetchInterval: WATCH_REFRESH_MS,
  refetchIntervalInBackground: false,
  // A refused or unreachable source is shown as a card state; three more
  // attempts would only delay that. The next poll tries again.
  retry: false,
} as const;

export function useLicenceWatchData(): UseQueryResult<Licence[], Error> {
  return useQuery({
    queryKey: WATCH_QUERY_KEYS.licences,
    queryFn: async () => (await api<{ licences: Licence[] }>("/api/licences")).licences,
    ...shared,
  });
}

export function useApplicationWatchData(): UseQueryResult<ResellerApplication[], Error> {
  return useQuery({
    queryKey: WATCH_QUERY_KEYS.applications,
    queryFn: async () =>
      (await api<ListResellerApplicationsResponse>("/api/admin/reseller-applications?status=open")).applications,
    ...shared,
  });
}

export function useReleaseWatchData(): UseQueryResult<ReleaseDTO[], Error> {
  return useQuery({
    queryKey: WATCH_QUERY_KEYS.releases,
    queryFn: async () => {
      const body = await api<{ data?: ReleaseDTO[]; releases?: ReleaseDTO[] }>("/api/documentation/admin/releases");
      return body.data ?? body.releases ?? [];
    },
    ...shared,
  });
}
